/**
 * TenFrameTool.jsx — Cycle 55 · Teach & Explain
 * ──────────────────────────────────────────────
 * Ten frame (single or double). Tap a cell to fill / empty. Ideal for
 * subitising, number bonds to 10 and bonds to 20.
 *
 * State: ?frames=2&filled=6:4  (dash-separated cells per frame,
 *        each with count filled from top-left row-major)
 */
import React from "react";
import { useSearchParams } from "react-router-dom";
import ManipulativeFrame from "@/components/tools/ManipulativeFrame";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

// Prop-driven display.
export function TenFrameDisplay({ frames, filled, setFilled, readOnly = false }) {
  const total = filled.reduce((s, n) => s + (n || 0), 0);
  const toggle = (frameIdx, cellIdx) => {
    if (readOnly) return;
    // Tapping cell N: if within filled range, set filled to N-1; else set to N.
    const cur = filled[frameIdx] || 0;
    const next = [...filled];
    next[frameIdx] = cellIdx < cur ? cellIdx : cellIdx + 1;
    setFilled(next);
  };

  return (
    <div className="rounded-3xl border-[3px] border-slate-900 bg-white p-4 md:p-6 shadow-[4px_4px_0_0_rgba(15,23,42,1)]" data-testid="tf-canvas">
      <div className={`grid ${frames === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"} gap-4`}>
        {Array.from({ length: frames }).map((_, f) => {
          const cur = filled[f] || 0;
          return (
            <div key={f} className="mx-auto" data-testid={`tf-frame-${f}`}>
              <p className="text-center text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                Frame {f + 1} · {cur}/10
              </p>
              <div className="grid grid-cols-5 gap-1 p-1 rounded-2xl border-[3px] border-slate-900 bg-slate-100"
                   style={{ width: "min(100%, 320px)" }}>
                {Array.from({ length: 10 }).map((_, i) => {
                  const isFilled = i < cur;
                  return (
                    <button key={i} type="button" onClick={() => toggle(f, i)}
                            data-testid={`tf-cell-${f}-${i}`}
                            aria-pressed={isFilled}
                            className="aspect-square rounded-lg border-2 border-slate-900 bg-white flex items-center justify-center">
                      {isFilled && (
                        <span className="h-3/4 w-3/4 rounded-full bg-red-400 border-2 border-slate-900 shadow-[0_1px_0_0_rgba(15,23,42,1)]" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-center font-heading text-2xl md:text-3xl font-black tabular-nums text-slate-900" data-testid="tf-total">
        Total: {total}
      </p>
    </div>
  );
}

export default function TenFrameTool() {
  const [params, setParams] = useSearchParams();
  const frames = clampInt(params.get("frames"), 1, 2, 1);
  const filled = (params.get("filled") || "0:0").split(":").map((n) => clampInt(n, 0, 10, 0));
  while (filled.length < frames) filled.push(0);
  filled.length = frames;

  const update = (patch) => {
    const q = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) q.delete(k); else q.set(k, String(v));
    }
    setParams(q, { replace: true });
  };
  const setFilled = (next) => update({ filled: next.join(":") });

  const settingsPanel = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-xs font-bold text-slate-700">Frames
        <select value={frames} data-testid="tf-frames"
                onChange={(e) => update({ frames: e.target.value })}
                className="ml-2 h-9 px-2 rounded-xl border-2 border-slate-900 bg-white font-bold text-sm">
          <option value="1">1 (Single)</option>
          <option value="2">2 (Double)</option>
        </select>
      </label>
    </div>
  );

  return (
    <ManipulativeFrame
      slug="ten-frame"
      title="Ten Frame"
      description="Tap a cell to fill / empty. Great for subitising and number bonds to 10 or 20."
      onReset={() => setFilled(Array.from({ length: frames }, () => 0))}
      settingsPanel={settingsPanel}
      shareUrl={window.location.href}
    >
      <TenFrameDisplay frames={frames} filled={filled} setFilled={setFilled} />
    </ManipulativeFrame>
  );
}
